import React, { useState, useEffect } from "react";
import { useAuth } from "@/src/lib/AuthContext";
import { db } from "@/src/lib/firebase";
import { collection, query, where, getDocs, doc, setDoc, getDoc, limit, orderBy } from "firebase/firestore";
import { MallClubCard as MallClubCardType, MallEvent, MallPromotion, Shop } from "@/src/types";
import { MallClubCard as LoyaltyCard } from "@/src/components/LoyaltyCard";
import { ShopCard } from "@/src/components/ShopCard";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { 
  Calendar, 
  Gift, 
  Ticket, 
  Star, 
  ChevronRight, 
  LayoutGrid, 
  Users, 
  Sparkles,
  Loader2,
  Clock,
  MapPin,
  ExternalLink,
  Cake,
  Store
} from "lucide-react";
import { motion } from "motion/react";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";

export function MallClubPage() {
  const { user, profile } = useAuth();
  const [clubCard, setClubCard] = useState<MallClubCardType | null>(null);
  const [events, setEvents] = useState<MallEvent[]>([]);
  const [promotions, setPromotions] = useState<MallPromotion[]>([]);
  const [affiliatedShops, setAffiliatedShops] = useState<Shop[]>([]);
  const [loading, setLoading] = useState(true);
  const [isJoining, setIsJoining] = useState(false);
  const [isBirthday, setIsBirthday] = useState(false);

  useEffect(() => {
    if (profile?.birthDate) {
      const today = new Date();
      const birthDate = new Date(profile.birthDate);
      if (today.getDate() === (birthDate.getDate() + 1) && today.getMonth() === birthDate.getMonth()) {
        // Offset adjustment might be needed depending on how Input type="date" handles timezones
        // Standard check:
        // const bDay = new Date(profile.birthDate);
        // if (today.getMonth() === bDay.getMonth() && today.getDate() === bDay.getDate()) ...
      }

      // Simpler check using strings
      const todayStr = new Date().toISOString().substring(5, 10); // MM-DD
      const birthStr = profile.birthDate.substring(5, 10);
      if (todayStr === birthStr) {
        setIsBirthday(true);
      }
    }
  }, [profile]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // 1. Fetch Club Card
        if (user) {
          const cardDoc = await getDoc(doc(db, "mall_club", user.uid));
          if (cardDoc.exists()) {
            setClubCard(cardDoc.data() as MallClubCardType);
          }
        }

        // 2. Fetch Events
        const eventsQuery = query(
          collection(db, "mall_events"), 
          where("active", "==", true),
          orderBy("date", "asc"),
          limit(10)
        );
        const eventsSnap = await getDocs(eventsQuery);
        setEvents(eventsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }) as MallEvent));

        // 3. Fetch Promotions
        const promosQuery = query(
          collection(db, "mall_promotions"), 
          where("active", "==", true),
          orderBy("expiryDate", "asc"),
          limit(10)
        );
        const promosSnap = await getDocs(promosQuery);
        setPromotions(promosSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }) as MallPromotion));

        // 4. Fetch Affiliated Shops
        const shopsQuery = query(
          collection(db, "shops"),
          where("isClubAffiliated", "==", true),
          limit(12)
        );
        const shopsSnap = await getDocs(shopsQuery);
        setAffiliatedShops(shopsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }) as Shop));

      } catch (error) {
        console.error("Error fetching club data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const handleJoinClub = async () => {
    if (!user) {
      toast.error("Debes iniciar sesión para unirte al club");
      return;
    }

    setIsJoining(true);
    try {
      const cardId = `EM-${Math.random().toString(36).substring(2, 7).toUpperCase()}`;
      const newCard: MallClubCardType = {
        uid: user.uid,
        cardId,
        tier: "members",
        points: 100, // Welcome points
        joinedAt: new Date().toISOString()
      };

      await setDoc(doc(db, "mall_club", user.uid), newCard);
      setClubCard(newCard);
      toast.success("¡Bienvenido al Mall Virtual! Has ganado 100 puntos de bienvenida.");
    } catch (error) {
      toast.error("Error al unirte al club");
      console.error(error);
    } finally {
      setIsJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-muted-foreground animate-pulse">Cargando beneficios del club...</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <header className="mb-12 text-center space-y-4">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider"
        >
          <Sparkles className="h-3 w-3" />
          Membresía Exclusiva
        </motion.div>
        <h1 className="text-4xl md:text-5xl font-black tracking-tight">Mall Virtual</h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          Fidelizamos tu pasión emprendedora. Accede a eventos exclusivos, promociones especiales y una red de tiendas premium.
        </p>
      </header>

      {isBirthday && clubCard && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="mb-12 relative overflow-hidden rounded-3xl bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 p-8 text-white shadow-2xl"
        >
          <div className="absolute top-0 right-0 p-4">
            <Cake className="h-24 w-24 opacity-20 rotate-12" />
          </div>
          <div className="relative z-10 flex flex-col md:flex-row items-center gap-8">
            <div className="bg-white/20 backdrop-blur-xl p-4 rounded-full">
              <Cake className="h-12 w-12" />
            </div>
            <div className="text-center md:text-left flex-1">
              <h2 className="text-3xl font-black mb-2">¡Feliz Cumpleaños, {profile?.displayName || "Socio"}!</h2>
              <p className="text-white/90 font-medium mb-4">
                Como agradecimiento por ser parte de nuestro club, hoy tienes un regalo especial.
              </p>
              <div className="inline-flex flex-col md:flex-row items-center gap-4 bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/20">
                <div className="flex flex-col items-center px-4 border-r border-white/20">
                  <span className="text-4xl font-extrabold uppercase leading-none">10%</span>
                  <span className="text-[10px] uppercase font-bold tracking-widest opacity-80">Descuento</span>
                </div>
                <div className="flex flex-col items-center px-4">
                  <span className="text-[10px] uppercase font-bold tracking-widest opacity-80 mb-2">Usa el código</span>
                  <div className="flex items-center gap-2 bg-white/20 px-4 py-1 rounded-lg">
                    <span className="font-mono text-xl font-bold tracking-wider">BDAY2024</span>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8 text-white hover:bg-white/20"
                      onClick={() => {
                        navigator.clipboard.writeText("BDAY2024");
                        toast.success("¡Código copiado! Feliz cumpleaños");
                      }}
                    >
                      <Ticket className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            <div className="shrink-0">
              <Button size="lg" variant="secondary" className="font-bold shadow-lg shadow-black/20">
                ¡Ir a comprar!
              </Button>
            </div>
          </div>
          {/* Confetti decoration bits can be imagined or added via CSS */}
        </motion.div>
      )}

      <div className="grid lg:grid-cols-3 gap-12 items-start">
        {/* Card Section */}
        <div className="lg:sticky lg:top-24 space-y-8">
          <div className="flex flex-col items-center gap-6">
            {clubCard ? (
              <LoyaltyCard card={clubCard} userName={user?.displayName || "Emprendedor"} />
            ) : (
              <Card className="w-full border-dashed border-2 bg-muted/30">
                <CardContent className="p-8 text-center space-y-4">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                    <Ticket className="h-8 w-8 text-primary" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="font-bold text-xl">¿Aún no eres miembro?</h3>
                    <p className="text-sm text-muted-foreground">
                      Únete hoy y obtén 100 puntos de bienvenida, acceso a preventas y descuentos exclusivos en todas las tiendas.
                    </p>
                  </div>
                  <Button 
                    className="w-full" 
                    onClick={handleJoinClub}
                    disabled={isJoining}
                  >
                    {isJoining && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Obtener mi Tarjeta Club
                  </Button>
                </CardContent>
              </Card>
            )}

            <div className="w-full space-y-4">
              <h4 className="font-bold text-sm uppercase tracking-widest text-muted-foreground px-1">Ventajas de tu nivel</h4>
              <div className="grid gap-3">
                {[
                  { icon: Gift, text: "Regalos mensuales sorpresa" },
                  { icon: Calendar, text: "Acceso prioritario a eventos" },
                  { icon: Star, text: "Multiplicador de puntos x1.2" }
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-card border">
                    <item.icon className="h-5 w-5 text-primary" />
                    <span className="text-sm font-medium">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="lg:col-span-2 space-y-8">
          <Tabs defaultValue="tiendas" className="w-full">
            <TabsList className="w-full grid grid-cols-3 h-12">
              <TabsTrigger value="tiendas" className="gap-2">
                <Store className="h-4 w-4" />
                Tiendas
              </TabsTrigger>
              <TabsTrigger value="events" className="gap-2">
                <Calendar className="h-4 w-4" />
                Eventos
              </TabsTrigger>
              <TabsTrigger value="promos" className="gap-2">
                <Ticket className="h-4 w-4" />
                Promociones
              </TabsTrigger>
            </TabsList>

            <TabsContent value="tiendas" className="mt-8">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {affiliatedShops.length > 0 ? (
                  affiliatedShops.map((shop) => (
                    <motion.div
                      key={shop.id}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <ShopCard shop={shop} />
                    </motion.div>
                  ))
                ) : (
                  <div className="md:col-span-2 lg:col-span-3 text-center py-12 border-2 border-dashed rounded-2xl bg-muted/20">
                    <LayoutGrid className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                    <p className="text-muted-foreground">Aún no hay tiendas afiliadas al club.</p>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="events" className="mt-8">
              <div className="grid gap-6">
                {events.length > 0 ? (
                  events.map((event) => (
                    <motion.div
                      key={event.id}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <Card className="overflow-hidden group hover:border-primary/50 transition-colors">
                        <div className="md:flex">
                          <div className="md:w-48 h-48 md:h-auto shrink-0 relative bg-muted">
                            {event.image ? (
                              <img 
                                src={event.image} 
                                alt={event.title} 
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Calendar className="h-12 w-12 text-muted-foreground/30" />
                              </div>
                            )}
                            <Badge className="absolute top-2 left-2 uppercase text-[10px]">
                              {event.type}
                            </Badge>
                          </div>
                          <CardContent className="p-6 flex flex-col justify-between flex-grow">
                            <div>
                              <div className="flex justify-between items-start mb-2">
                                <h3 className="font-bold text-xl">{event.title}</h3>
                                {event.exclusiveFor !== 'members' && (
                                  <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 border-yellow-200">
                                    Nivel {event.exclusiveFor}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                                {event.description}
                              </p>
                            </div>
                            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                              <div className="flex items-center gap-1.5 font-medium text-foreground">
                                <Clock className="h-4 w-4 text-primary" />
                                {format(new Date(event.date), "PPP p", { locale: es })}
                              </div>
                              <div className="flex items-center gap-1.5 font-medium text-foreground">
                                <MapPin className="h-4 w-4 text-primary" />
                                {event.location}
                              </div>
                            </div>
                          </CardContent>
                          <div className="p-6 md:border-l flex flex-col items-center justify-center gap-2 shrink-0 bg-slate-50/50">
                            <Button size="sm" variant="outline" className="w-full font-bold">
                              Ver más
                            </Button>
                            {!clubCard && event.exclusiveFor !== 'members' && (
                              <p className="text-[10px] text-center text-muted-foreground">
                                Requiere Club
                              </p>
                            )}
                          </div>
                        </div>
                      </Card>
                    </motion.div>
                  ))
                ) : (
                  <div className="text-center py-12 border-2 border-dashed rounded-2xl bg-muted/20">
                    <Calendar className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                    <p className="text-muted-foreground">No hay eventos próximos programados.</p>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="promos" className="mt-8">
              <div className="grid md:grid-cols-2 gap-6">
                {promotions.length > 0 ? (
                  promotions.map((promo) => (
                    <motion.div
                      key={promo.id}
                      initial={{ opacity: 0, scale: 0.95 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                    >
                      <Card className="relative overflow-hidden border-2 h-full flex flex-col">
                        {promo.exclusiveForClub && (
                          <div className="absolute top-0 right-0 bg-primary text-white text-[10px] font-black px-3 py-1 uppercase tracking-tighter rounded-bl-lg z-10">
                            Exclusivo Club
                          </div>
                        )}
                        <CardHeader className="pb-2">
                          <div className="text-xs font-bold text-primary flex items-center gap-1 mb-1">
                            <ExternalLink className="h-3 w-3" />
                            {promo.shopName}
                          </div>
                          <CardTitle className="text-xl">{promo.title}</CardTitle>
                          <CardDescription className="line-clamp-2">{promo.description}</CardDescription>
                        </CardHeader>
                        <CardContent className="flex-grow flex flex-col justify-center items-center py-6 bg-slate-50 border-y space-y-3">
                          <div className="text-3xl font-black text-foreground">{promo.discountValue}</div>
                          {promo.discountCode && (
                            <div className="flex items-center gap-2 bg-white border border-dashed px-4 py-2 rounded-lg">
                              <span className="font-mono font-bold text-lg select-all">{promo.discountCode}</span>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-8 w-8 hover:bg-slate-100"
                                onClick={() => {
                                  navigator.clipboard.writeText(promo.discountCode!);
                                  toast.success("Código copiado");
                                }}
                              >
                                <Ticket className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </CardContent>
                        <CardFooter className="py-4 flex justify-between items-center text-xs text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Clock className="h-3.5 w-3.5" />
                            Expira: {format(new Date(promo.expiryDate), "PP", { locale: es })}
                          </div>
                          <Button variant="link" size="sm" className="h-auto p-0 font-bold">
                            Ir a la tienda
                          </Button>
                        </CardFooter>
                      </Card>
                    </motion.div>
                  ))
                ) : (
                  <div className="col-span-full text-center py-12 border-2 border-dashed rounded-2xl bg-muted/20">
                    <Gift className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                    <p className="text-muted-foreground">Pronto tendremos nuevas promociones para ti.</p>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>

          {/* Integration with Shops */}
          <div className="pt-8">
            <Card className="bg-primary text-primary-foreground overflow-hidden">
              <div className="md:flex items-center p-8 gap-8">
                <div className="mb-6 md:mb-0 shrink-0">
                  <div className="w-20 h-20 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center">
                    <Users className="h-10 w-10" />
                  </div>
                </div>
                <div className="space-y-4 flex-grow">
                  <h3 className="text-2xl font-black tracking-tight">Comunidad de Fidelización</h3>
                  <p className="opacity-90 max-w-xl">
                    Nuestras más de 50 tiendas virtuales están integradas al sistema de puntos. Cada compra suma niveles para tu tarjeta.
                  </p>
                  <div className="flex flex-wrap gap-4 pt-2">
                    <Button variant="secondary" font-bold>Ver tiendas participantes</Button>
                  </div>
                </div>
              </div>
              {/* Pattern Bottom */}
              <div className="h-2 bg-white/10" />
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
